// src/components/BackingTrackPlayer.jsx
// Plays a backing track (YouTube embed or direct mp3/audio URL)
// Handles YouTube embed-blocked videos with "Open in YouTube" fallback
// Auto-marks song as played when track ends

import React, { useEffect, useRef, useState } from 'react';

const extractYouTubeId = (url) => {
  if (!url) return null;
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]{11})/,
    /youtube\.com\/shorts\/([^&\n?#]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const isAudioUrl = (url) =>
  url && (/\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(url) ||
    url.startsWith('blob:') || url.startsWith('data:audio'));

export default function BackingTrackPlayer({ song, onMarkPlayed, onRemoveTrack, onToggle }) {
  const url = song?.backingTrackUrl || '';
  const youtubeId = extractYouTubeId(url);
  const isYoutube = !!youtubeId;
  const isAudio = !isYoutube && isAudioUrl(url);

  const iframeRef = useRef(null);
  const audioRef = useRef(null);

  const [embedBlocked, setEmbedBlocked] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioError, setAudioError] = useState(false);

  // ── YouTube postMessage: detect end + blocked ─────────────
  useEffect(() => {
    if (!isYoutube) return;
    const handleMessage = (e) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (!data || data.event !== 'onStateChange') return;
        if (data.info === 0) {
          // Ended → auto-mark played
          onMarkPlayed && onMarkPlayed();
        }
        if (data.info === -1 && iframeLoaded) {
          // Unstarted after load = embed blocked
          setEmbedBlocked(true);
        }
      } catch (_) {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isYoutube, iframeLoaded, onMarkPlayed]);

  // Also detect via YouTube error postMessage event
  useEffect(() => {
    if (!isYoutube) return;
    const handleMessage = (e) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.event === 'onError') setEmbedBlocked(true);
      } catch (_) {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isYoutube]);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    // Ping the iframe to start receiving events
    setTimeout(() => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'listening', id: 1 }), '*'
        );
      } catch (_) {}
    }, 500);
  };

  // ── Audio helpers ─────────────────────────────────────────
  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioPlaying) { audioRef.current.pause(); setAudioPlaying(false); }
    else { audioRef.current.play(); setAudioPlaying(true); }
  };

  const handleAudioTimeUpdate = () => {
    if (!audioRef.current) return;
    setAudioProgress(audioRef.current.currentTime);
    setAudioDuration(audioRef.current.duration || 0);
  };

  const handleAudioEnded = () => {
    setAudioPlaying(false);
    onMarkPlayed && onMarkPlayed();
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !audioDuration) return;
    const pct = parseFloat(e.target.value);
    audioRef.current.currentTime = (pct / 100) * audioDuration;
    setAudioProgress(audioRef.current.currentTime);
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  if (!url) return null;

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(124,58,237,0.3)', backdropFilter: 'blur(8px)' }}>

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span style={{ color: '#a855f7' }}>🎵</span>
          <span className="text-white text-xs font-bold truncate">
            {song.backingTrackName || song.title}
          </span>
        </div>
        <button onClick={() => onRemoveTrack && onRemoveTrack()}
          className="text-gray-500 hover:text-red-400 text-xs px-2 py-1 rounded transition-all flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.05)' }}>✕ Remove</button>
      </div>

      {/* ── YOUTUBE ── */}
      {isYoutube && (
        embedBlocked ? (
          /* Embed blocked fallback */
          <div className="p-5 text-center">
            <div className="text-4xl mb-2">🚫</div>
            <p className="text-white font-bold text-sm mb-1">Embedding Disabled</p>
            <p className="text-gray-400 text-xs mb-1">
              The video uploader has blocked embedding for this video.
            </p>
            <p className="text-gray-500 text-[11px] mb-4">
              You can still open it in YouTube and play it there.
            </p>
            <div className="flex flex-col gap-2.5">
              <a
                href={`https://www.youtube.com/watch?v=${youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-sm px-4 py-3 rounded-xl text-center transition-all"
                style={{
                  background: 'linear-gradient(135deg,#ff0000,#cc0000)',
                  color: '#fff',
                  border: '2px solid #ff4444',
                  boxShadow: '0 0 16px rgba(255,0,0,0.5)',
                  textDecoration: 'none',
                  display: 'block'
                }}
              >
                ▶ Open in YouTube
              </a>
              <button
                onClick={() => onMarkPlayed && onMarkPlayed()}
                className="font-bold text-sm px-4 py-2.5 rounded-xl transition-all"
                style={{
                  background: 'linear-gradient(135deg,#06b6d4,#0891b2)',
                  color: '#fff',
                  border: '2px solid #06b6d4',
                  boxShadow: '0 0 10px rgba(6,182,212,0.4)'
                }}
              >✓ Mark as Played</button>
            </div>
            <p className="text-gray-600 text-[10px] mt-4 leading-relaxed">
              💡 Tip: Use lyric videos or karaoke/backing track uploads when adding to the song bank — they rarely block embeds.
            </p>
          </div>
        ) : (
          /* Normal YouTube embed */
          <div>
            <div className="relative" style={{ paddingTop: '56.25%' }}>
              <iframe
                ref={iframeRef}
                src={`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&autoplay=1&rel=0&modestbranding=1`}
                title="Backing Track"
                allow="autoplay; encrypted-media"
                allowFullScreen
                onLoad={handleIframeLoad}
                className="absolute inset-0 w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <p className="text-gray-400 text-[10px]">Track will auto-mark played when it ends</p>
              <button
                onClick={() => onMarkPlayed && onMarkPlayed()}
                className="font-bold text-xs px-3 py-1.5 rounded-lg transition-all"
                style={{
                  background: 'linear-gradient(135deg,#06b6d4,#0891b2)',
                  color: '#fff',
                  border: '2px solid #06b6d4',
                  boxShadow: '0 0 8px rgba(6,182,212,0.4)'
                }}
              >MARK PLAYED</button>
            </div>
          </div>
        )
      )}

      {/* ── AUDIO ── */}
      {isAudio && (
        <div className="p-3">
          {audioError ? (
            <div className="text-center py-2">
              <p className="text-red-400 text-xs mb-2">⚠️ Could not load audio file</p>
              <button onClick={() => onMarkPlayed && onMarkPlayed()}
                className="font-bold text-xs px-3 py-1.5 rounded-lg"
                style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)', color: '#fff', border: '2px solid #06b6d4' }}
              >✓ Mark Played</button>
            </div>
          ) : (
            <div>
              <audio ref={audioRef} src={url}
                onTimeUpdate={handleAudioTimeUpdate}
                onEnded={handleAudioEnded}
                onError={() => setAudioError(true)}
                onLoadedMetadata={handleAudioTimeUpdate}
              />
              <input type="range" min="0" max="100"
                value={audioDuration ? (audioProgress / audioDuration) * 100 : 0}
                onChange={handleSeek}
                className="w-full mb-2 cursor-pointer"
                style={{ accentColor: '#a855f7', height: '4px' }}
              />
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[10px]">
                  {formatTime(audioProgress)} / {formatTime(audioDuration)}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={toggleAudio}
                    className="font-bold text-xs px-4 py-1.5 rounded-lg transition-all"
                    style={audioPlaying
                      ? { background: 'linear-gradient(135deg,#00ff88,#00d9a0)', color: '#000', border: '2px solid #00ff88', boxShadow: '0 0 10px rgba(0,255,136,0.5)' }
                      : { background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', border: '2px solid #a855f7', boxShadow: '0 0 10px rgba(168,85,247,0.5)' }
                    }
                  >{audioPlaying ? '⏸ Pause' : '▶ Play'}</button>
                  <button onClick={() => onMarkPlayed && onMarkPlayed()}
                    className="font-bold text-xs px-3 py-1.5 rounded-lg transition-all"
                    style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)', color: '#fff', border: '2px solid #06b6d4', boxShadow: '0 0 8px rgba(6,182,212,0.4)' }}
                  >✓ Mark Played</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── UNSUPPORTED ── */}
      {!isYoutube && !isAudio && (
        <div className="p-3 text-center">
          <p className="text-gray-400 text-xs mb-2">⚠️ Unsupported track format</p>
          <button onClick={() => onMarkPlayed && onMarkPlayed()}
            className="font-bold text-xs px-3 py-1.5 rounded-lg"
            style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)', color: '#fff', border: '2px solid #06b6d4' }}
          >✓ Mark Played</button>
        </div>
      )}

    </div>
  );
}
